import { Button, Menu, MenuItem } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faFile, faInfoCircle, faPaperPlane, faTimes } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AVAILABLE_MODELS, CONTEXT_HIGH_TOKENS, CONTEXT_WARN_TOKENS, MODEL_CONTEXT_WINDOWS } from '../../types';

interface IProps {
  onSend: (text: string, files: File[]) => void;
  onStop: () => void;
  isStreaming: boolean;
  model: string;
  onModelChange: (model: string) => void;
  autoFocus?: boolean;
  contextTokens?: number | null;
}

interface IAttachment {
  id: string;
  file: File;
  previewUrl: string;
}

const MessageInput: React.FC<IProps> = ({ onSend, onStop, isStreaming, model, onModelChange, autoFocus, contextTokens }) => {
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<IAttachment[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (autoFocus && textareaRef.current && !isStreaming) {
      textareaRef.current.focus();
    }
  }, [autoFocus, isStreaming]);

  const currentModel = AVAILABLE_MODELS.find((m) => m.id === model) || AVAILABLE_MODELS[0];

  const tokens = contextTokens || 0;
  const windowTokens = MODEL_CONTEXT_WINDOWS[model] || 1000000;
  const warnAt = Math.min(CONTEXT_WARN_TOKENS, Math.round(windowTokens * 0.5));
  const highAt = Math.min(CONTEXT_HIGH_TOKENS, Math.round(windowTokens * 0.75));
  const contextLabel = `${Math.round(tokens / 1000)}k`;
  const contextLevel: 'ok' | 'warn' | 'high' =
    tokens >= highAt ? 'high' : tokens >= warnAt ? 'warn' : 'ok';
  const contextHint = contextLevel === 'high'
    ? `This session is very long (~${contextLabel} tokens of context). At this length response quality measurably degrades and every reply gets slower — start a new chat, optionally asking for a summary to carry over.`
    : contextLevel === 'warn'
      ? `This session is getting long (~${contextLabel} tokens of context). Model accuracy and speed degrade gradually as a session grows — consider starting a new chat for new topics.`
      : `Context used by this session so far (~${contextLabel} tokens). Performance is unaffected at this size.`;

  const addFiles = (files: FileList | File[]) => {
    const newAttachments = Array.from(files).map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
    }));
    setAttachments((prev) => [...prev, ...newAttachments]);
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => {
      const att = prev.find((a) => a.id === id);
      if (att) URL.revokeObjectURL(att.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  };

  const handleSend = () => {
    const trimmed = text.trim();
    if ((!trimmed && attachments.length === 0) || isStreaming) return;
    onSend(trimmed, attachments.map((a) => a.file));
    setText('');
    attachments.forEach((a) => URL.revokeObjectURL(a.previewUrl));
    setAttachments([]);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 150) + 'px';
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pastedFiles = Array.from(e.clipboardData.items)
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter((f): f is File => f !== null);
    if (pastedFiles.length > 0) addFiles(pastedFiles);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const handleModelSelect = (modelId: string) => {
    onModelChange(modelId);
    setMenuAnchor(null);
  };

  return (
    <Wrapper $isDragging={isDragging} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
      {attachments.length > 0 && (
        <AttachmentsStrip>
          {attachments.map((att) => (
            <AttachmentItem key={att.id} $isImage={!!att.previewUrl}>
              {att.previewUrl ? (
                <AttachmentThumb src={att.previewUrl} alt={att.file.name} />
              ) : (
                <FileChipInner>
                  <FontAwesomeIcon icon={faFile} />
                  <FileChipName>{att.file.name}</FileChipName>
                </FileChipInner>
              )}
              <RemoveBtn onClick={() => removeAttachment(att.id)} title="Remove">
                <FontAwesomeIcon icon={faTimes} />
              </RemoveBtn>
            </AttachmentItem>
          ))}
        </AttachmentsStrip>
      )}
      <BottomRow>
        <InputRow>
          <ModelChip onClick={(e) => setMenuAnchor(e.currentTarget)} title={currentModel.label}>
            {currentModel.abbr}
          </ModelChip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            {AVAILABLE_MODELS.map((m) => (
              <ModelMenuItem key={m.id} selected={m.id === model} onClick={() => handleModelSelect(m.id)}>
                <ModelMenuLabel>{m.label}</ModelMenuLabel>
                <ModelMenuHint>{m.hint}</ModelMenuHint>
              </ModelMenuItem>
            ))}
          </Menu>
          {tokens > 0 && (
            <ContextBadge $level={contextLevel} title={contextHint}>
              <FontAwesomeIcon icon={contextLevel === 'ok' ? faInfoCircle : faExclamationTriangle} />
              <span>{contextLabel}</span>
            </ContextBadge>
          )}
          <TextArea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            onPaste={handlePaste}
            placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
            rows={1}
            disabled={isStreaming}
          />
        </InputRow>
        {isStreaming ? (
          <Button variant="outlined" color="secondary" size="small" onClick={onStop}>
            Stop
          </Button>
        ) : (
          <SendButton onClick={handleSend} disabled={!text.trim() && attachments.length === 0} title="Send message">
            <FontAwesomeIcon icon={faPaperPlane} size="sm" />
          </SendButton>
        )}
      </BottomRow>
    </Wrapper>
  );
};

export default MessageInput;

const Wrapper = styled.div<{ $isDragging: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-radius: 8px;
  outline: 2px solid ${(p) => (p.$isDragging ? '#1976d2' : 'transparent')};
  background: ${(p) => (p.$isDragging ? 'rgba(25, 118, 210, 0.04)' : 'transparent')};
  transition: outline-color 0.15s, background 0.15s;
`;

const BottomRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;

const InputRow = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: 6px;
`;

const AttachmentsStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 2px;
`;

const AttachmentItem = styled.div<{ $isImage: boolean }>`
  position: relative;
  height: 64px;
  width: ${(p) => (p.$isImage ? '64px' : 'auto')};
  min-width: ${(p) => (p.$isImage ? 'auto' : '80px')};
  max-width: ${(p) => (p.$isImage ? 'auto' : '160px')};
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  flex-shrink: 0;
`;

const FileChipInner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 20px 6px 8px;
  background: #f5f5f5;
  color: #555;
  font-size: 18px;
`;

const FileChipName = styled.span`
  font-size: 10px;
  color: #666;
  text-align: center;
  word-break: break-all;
  line-height: 1.2;
  max-height: 2.4em;
  overflow: hidden;
`;

const AttachmentThumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const RemoveBtn = styled.button`
  position: absolute;
  top: 3px;
  right: 3px;
  width: 18px;
  height: 18px;
  padding: 0;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const ModelChip = styled.button`
  flex-shrink: 0;
  background: #e8e8e8;
  border: none;
  border-radius: 4px;
  padding: 4px 6px;
  font-size: 11px;
  font-weight: 600;
  color: #555;
  cursor: pointer;
  white-space: nowrap;
  margin-bottom: 6px;
  &:hover {
    background: #d0d0d0;
  }
`;

const ContextBadge = styled.div<{ $level: 'ok' | 'warn' | 'high' }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  padding: 4px 6px;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 6px;
  cursor: help;
  color: ${(p) => (p.$level === 'high' ? '#c62828' : p.$level === 'warn' ? '#b26a00' : '#2e7d32')};
  background: ${(p) => (p.$level === 'high' ? '#ffebee' : p.$level === 'warn' ? '#fff3e0' : '#e8f5e9')};
`;

const ModelMenuItem = styled(MenuItem)`
  && {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 8px 16px;
  }
`;

const ModelMenuLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
`;

const ModelMenuHint = styled.span`
  font-size: 11px;
  color: #999;
`;

const SendButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  background: #1976d2;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s;
  &:hover:not(:disabled) {
    background: #1565c0;
  }
  &:disabled {
    background: #ccc;
    cursor: default;
  }
`;

const TextArea = styled.textarea`
  flex: 1;
  resize: none;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.4;
  outline: none;
  &:focus {
    border-color: #1976d2;
  }
  &:disabled {
    background: #f9f9f9;
  }
`;
